import { FC } from 'react';
import RefreshOutlined from '@mui/icons-material/RefreshOutlined';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import { dailyNavAsOfLabel } from 'common/constants/dailyNav';
import { isPendingJob, providersWhere } from 'pages/Intelligence/DailyNav/components/ReportDialog/ReportDialog.utils';
import type { DailyNavMarketDataReadiness } from 'pages/Intelligence/DailyNav/daily-nav.api.types';
import { DAILY_NAV_TOOLTIP_SLOT_PROPS } from 'pages/Intelligence/DailyNav/daily-nav.constants';
import { MarketDataFreshnessProps } from './MarketDataFreshness.props';

const refreshSpin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const FINALIZED_AS_OF_JUSTIFICATION =
  'This day has been finalized. Public comps and secondary prices are read as they existed at the finalize time, or at the market close or Data Cutoff Time if that came first, so later market revisions cannot change numbers that were already signed off.';

const OPEN_AS_OF_JUSTIFICATION =
  'This day is not finalized. Shows when the page last refreshed; after the market close, or the Data Cutoff Time if earlier, it shows that time, since nothing newer is shown after it. Prices captured at the cutoff can take a few minutes to appear, and finalizing waits until they have. Refresh re-queries the latest data.';

// The earlier of the page's last refresh and the data cutoff: once the cutoff has
// passed, refreshing again cannot make the data any fresher than the cutoff.
const resolveOpenDayAsOf = (sessionClose?: string | null, refreshedAt?: number): string | null => {
  const refreshed = refreshedAt ? new Date(refreshedAt) : null;
  const close = sessionClose ? new Date(sessionClose) : null;
  if (refreshed && close && !Number.isNaN(close.getTime())) {
    return (refreshed < close ? refreshed : close).toISOString();
  }
  return refreshed?.toISOString() ?? sessionClose ?? null;
};

// A short note and its explanation while data is in flight or incomplete; null when settled.
const resolveRunNote = (marketData?: DailyNavMarketDataReadiness | null) => {
  if (!marketData) {
    return null;
  }
  const loading = providersWhere(marketData, isPendingJob) || 'Market data';
  switch (marketData.status) {
    case 'pending':
      return {
        text: `${loading} update loading`,
        detail: `${loading} is still loading its latest prices, so some prices may be from its previous run. This page will update on its own when they arrive. This day can't be finalized until then.`,
      };
    case 'stalled':
      return {
        text: `${loading} update delayed`,
        detail: `${loading} has been loading for over 15 minutes, so some prices may be from its previous run. This day can be finalized, but those prices may still change afterwards.`,
      };
    case 'failed': {
      const failed = providersWhere(marketData, status => status === 'FAILED') || 'A data provider';
      return {
        text: `${failed} update failed`,
        detail: `${failed} did not finish its latest update, so some prices are from earlier runs. They will not change.`,
      };
    }
    case 'unavailable':
      return {
        text: 'Latest update not confirmed',
        detail:
          'Could not confirm whether the latest market data updates have finished, so some prices may be from an earlier run. Refresh to check again.',
      };
    default:
      return null;
  }
};

const MarketDataFreshness: FC<MarketDataFreshnessProps> = ({
  marketData,
  asOf,
  refreshedAt,
  isFinalized,
  isRefreshing,
  onRefresh,
}) => {
  const label = dailyNavAsOfLabel(isFinalized ? asOf : resolveOpenDayAsOf(asOf, refreshedAt));
  if (!label) {
    return null;
  }
  const runNote = isFinalized ? null : resolveRunNote(marketData);

  return (
    <Box display="flex" alignItems="center" gap={0.25}>
      <Tooltip
        arrow
        describeChild
        placement="bottom-start"
        slotProps={DAILY_NAV_TOOLTIP_SLOT_PROPS}
        title={
          <Typography variant="caption" data-testid="daily-nav-market-data-as-of-justification">
            {isFinalized ? FINALIZED_AS_OF_JUSTIFICATION : OPEN_AS_OF_JUSTIFICATION}
          </Typography>
        }>
        <Typography
          variant="caption"
          color="text.secondary"
          data-testid="daily-nav-market-data-as-of"
          sx={{ borderBottom: '1px dashed', borderColor: 'text.disabled', cursor: 'help' }}>
          {isFinalized ? 'Market data as of' : 'Last refreshed'} {label}
        </Typography>
      </Tooltip>
      {runNote && (
        <Tooltip arrow describeChild slotProps={DAILY_NAV_TOOLTIP_SLOT_PROPS} title={runNote.detail}>
          <Typography
            variant="caption"
            data-testid="daily-nav-market-data-run-note"
            sx={{ color: marketData?.status === 'unavailable' ? 'text.secondary' : 'warning.main', cursor: 'help' }}>
            · {runNote.text}
          </Typography>
        </Tooltip>
      )}
      {!isFinalized && (
        <Tooltip title="Refresh market data">
          <span>
            <IconButton
              aria-label="Refresh market data"
              data-testid="daily-nav-refresh-market-data"
              disabled={isRefreshing}
              onClick={onRefresh}
              size="small"
              sx={{ p: 0.25 }}>
              <RefreshOutlined
                sx={{
                  fontSize: 14,
                  animation: isRefreshing ? `${refreshSpin} 0.8s linear infinite` : undefined,
                }}
              />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Box>
  );
};

export default MarketDataFreshness;
